/**
 * Math Utilities using decimal.js for precision
 *
 * This module provides decimal-precision arithmetic operations to avoid
 * JavaScript's floating-point precision errors in financial calculations.
 *
 * All functions accept numbers, strings, or null/undefined and return JavaScript numbers.
 * Use these functions instead of native Math operations for all financial and quantity calculations.
 *
 * @module mathUtils
 */

import Decimal from "decimal.js";

/**
 * Convert a value to a Decimal instance
 * @param {number|string|null|undefined} value - Value to convert
 * @returns {Decimal} Decimal instance (0 if value is null/undefined)
 */
export const toDecimal = value => {
  if (value === null || value === undefined || value === "") {
    return new Decimal(0);
  }
  return new Decimal(value);
};

/**
 * Add multiple values with decimal precision
 * @param {...(number|string|null|undefined)} values - Values to add
 * @returns {number} Sum as JavaScript number
 * @example
 * add(0.1, 0.2) // returns 0.3 (not 0.30000000000000004)
 * add(10, 20, 30) // returns 60
 * add(10, null, 20) // returns 30
 */
export const add = (...values) => {
  return values
    .reduce((sum, val) => {
      return toDecimal(sum).plus(toDecimal(val));
    }, new Decimal(0))
    .toNumber();
};

/**
 * Subtract values with decimal precision
 * @param {number|string|null|undefined} a - Minuend
 * @param {number|string|null|undefined} b - Subtrahend
 * @returns {number} Difference as JavaScript number
 * @example
 * subtract(0.3, 0.1) // returns 0.2 (not 0.19999999999999998)
 */
export const subtract = (a, b) => {
  return toDecimal(a)
    .minus(toDecimal(b))
    .toNumber();
};

/**
 * Multiply values with decimal precision
 * Supports multiple arguments
 * @param {...(number|string|null|undefined)} values - Values to multiply
 * @returns {number} Product as JavaScript number
 * @example
 * multiply(0.1, 0.2) // returns 0.02
 * multiply(2, 3, 4) // returns 24
 */
export const multiply = (...values) => {
  return values
    .reduce((product, val) => {
      return toDecimal(product).times(toDecimal(val));
    }, new Decimal(1))
    .toNumber();
};

/**
 * Divide values with decimal precision
 * @param {number|string|null|undefined} a - Dividend
 * @param {number|string|null|undefined} b - Divisor
 * @returns {number} Quotient as JavaScript number (Infinity if b is 0)
 * @example
 * divide(0.3, 0.1) // returns 3
 * divide(10, 0) // returns Infinity
 */
export const divide = (a, b) => {
  const divisor = toDecimal(b);
  if (divisor.isZero()) {
    return Infinity;
  }
  return toDecimal(a)
    .div(divisor)
    .toNumber();
};

/**
 * Round a value to specified decimal places
 * @param {number|string|null|undefined} value - Value to round
 * @param {number} [decimalPlaces=0] - Number of decimal places
 * @returns {number} Rounded value as JavaScript number
 * @example
 * round(1.2345, 2) // returns 1.23
 * round(1.5) // returns 2
 */
export const round = (value, decimalPlaces = 0) => {
  return toDecimal(value)
    .toDecimalPlaces(decimalPlaces)
    .toNumber();
};

/**
 * Round a value to 2 decimal places (cents)
 * Use this for all currency calculations
 * @param {number|string|null|undefined} value - Value to round
 * @returns {number} Value rounded to 2 decimal places
 * @example
 * roundToCents(1.235) // returns 1.24
 * roundToCents(10.1) // returns 10.10
 */
export const roundToCents = value => {
  return round(value, 2);
};

/**
 * Round a value to 3 decimal places
 * Use this for quantity calculations
 * @param {number|string|null|undefined} value - Value to round
 * @returns {number} Value rounded to 3 decimal places
 * @example
 * roundToThreeDecimals(1.2345) // returns 1.235
 */
export const roundToThreeDecimals = value => {
  return round(value, 3);
};

/**
 * Round up to next integer (ceiling)
 * @param {number|string|null|undefined} value - Value to round up
 * @returns {number} Ceiling value as JavaScript number
 * @example
 * ceil(1.1) // returns 2
 * ceil(1.9) // returns 2
 */
export const ceil = value => {
  return toDecimal(value)
    .ceil()
    .toNumber();
};

/**
 * Round down to previous integer (floor)
 * @param {number|string|null|undefined} value - Value to round down
 * @returns {number} Floor value as JavaScript number
 * @example
 * floor(1.1) // returns 1
 * floor(1.9) // returns 1
 */
export const floor = value => {
  return toDecimal(value)
    .floor()
    .toNumber();
};

/**
 * Get absolute value
 * @param {number|string|null|undefined} value - Value
 * @returns {number} Absolute value as JavaScript number
 * @example
 * abs(-5) // returns 5
 * abs(5) // returns 5
 */
export const abs = value => {
  return toDecimal(value)
    .abs()
    .toNumber();
};

/**
 * Get the sign of a number (-1, 0, or 1)
 * @param {number|string|null|undefined} value - Value to get sign of
 * @returns {number} -1 for negative, 0 for zero, 1 for positive
 * @example
 * sign(-5) // returns -1
 * sign(0) // returns 0
 * sign(5) // returns 1
 */
export const sign = value => {
  const decimal = toDecimal(value);
  if (decimal.isZero()) return 0;
  if (decimal.isNegative()) return -1;
  return 1;
};

/**
 * Get minimum value from multiple values
 * @param {...(number|string|null|undefined)} values - Values to compare
 * @returns {number} Minimum value as JavaScript number
 * @example
 * min(1, 2, 3) // returns 1
 * min(10, null, 5) // returns 0 (null treated as 0)
 */
export const min = (...values) => {
  if (values.length === 0) return 0;
  return Decimal.min(...values.map(toDecimal)).toNumber();
};

/**
 * Get maximum value from multiple values
 * @param {...(number|string|null|undefined)} values - Values to compare
 * @returns {number} Maximum value as JavaScript number
 * @example
 * max(1, 2, 3) // returns 3
 * max(10, null, 5) // returns 10
 */
export const max = (...values) => {
  if (values.length === 0) return 0;
  return Decimal.max(...values.map(toDecimal)).toNumber();
};

/**
 * Sum an array of values
 * Alias for add() but accepts an array
 * @param {Array<number|string|null|undefined>} values - Array of values to sum
 * @returns {number} Sum as JavaScript number
 * @example
 * sum([1, 2, 3]) // returns 6
 * sum([0.1, 0.2]) // returns 0.3
 */
export const sum = values => {
  if (!Array.isArray(values) || values.length === 0) {
    return 0;
  }
  return add(...values);
};

/**
 * Check if a value is zero (accounting for precision)
 * @param {number|string|null|undefined} value - Value to check
 * @returns {boolean} True if value is zero
 * @example
 * isZero(0) // returns true
 * isZero(0.0000001) // returns false
 * isZero(null) // returns true
 */
export const isZero = value => {
  return toDecimal(value).isZero();
};

/**
 * Compare two values for equality (accounting for precision)
 * @param {number|string|null|undefined} a - First value
 * @param {number|string|null|undefined} b - Second value
 * @returns {boolean} True if values are equal
 * @example
 * equals(0.1 + 0.2, 0.3) // returns true
 */
export const equals = (a, b) => {
  return toDecimal(a).equals(toDecimal(b));
};

/**
 * Check if a is greater than b
 * @param {number|string|null|undefined} a - First value
 * @param {number|string|null|undefined} b - Second value
 * @returns {boolean} True if a > b
 * @example
 * greaterThan(2, 1) // returns true
 */
export const greaterThan = (a, b) => {
  return toDecimal(a).greaterThan(toDecimal(b));
};

/**
 * Check if a is greater than or equal to b
 * @param {number|string|null|undefined} a - First value
 * @param {number|string|null|undefined} b - Second value
 * @returns {boolean} True if a >= b
 */
export const greaterThanOrEqualTo = (a, b) => {
  return toDecimal(a).greaterThanOrEqualTo(toDecimal(b));
};

/**
 * Check if a is less than b
 * @param {number|string|null|undefined} a - First value
 * @param {number|string|null|undefined} b - Second value
 * @returns {boolean} True if a < b
 */
export const lessThan = (a, b) => {
  return toDecimal(a).lessThan(toDecimal(b));
};

/**
 * Check if a is less than or equal to b
 * @param {number|string|null|undefined} a - First value
 * @param {number|string|null|undefined} b - Second value
 * @returns {boolean} True if a <= b
 */
export const lessThanOrEqualTo = (a, b) => {
  return toDecimal(a).lessThanOrEqualTo(toDecimal(b));
};

/**
 * Calculate percentage of a value
 * @param {number|string|null|undefined} value - Base value
 * @param {number|string|null|undefined} percent - Percentage (e.g., 10 for 10%)
 * @returns {number} Percentage amount as JavaScript number
 * @example
 * percentage(100, 10) // returns 10 (10% of 100)
 * percentage(50, 20) // returns 10 (20% of 50)
 */
export const percentage = (value, percent) => {
  return multiply(divide(percent, 100), value);
};

/**
 * Calculate what percentage one value is of another
 * @param {number|string|null|undefined} part - Part value
 * @param {number|string|null|undefined} whole - Whole value
 * @returns {number} Percentage as JavaScript number (0 if whole is 0)
 * @example
 * percentageOf(10, 100) // returns 10 (10 is 10% of 100)
 * percentageOf(25, 50) // returns 50 (25 is 50% of 50)
 */
export const percentageOf = (part, whole) => {
  if (isZero(whole)) {
    return 0;
  }
  return multiply(divide(part, whole), 100);
};
